// Synthesised audio: no assets, no libraries, every sound built out of
// oscillators and one cached noise buffer.
//
// The original GORILLA.BAS shipped no soundtrack. Its whole voice was a handful
// of short `PLAY` bursts beeped out of the PC speaker: a fanfare on the title
// screen, a blip as the banana left the gorilla's hand, a low burst on impact,
// and a dance for whoever won. The effects below are still that voice — square
// waves and nothing else — with one deliberate departure: the impact's burst is
// played as a single falling hit rather than seven separate blips. Seven blips
// 47ms apart is a stutter, and it was the dance's own figure, so an explosion
// came out sounding like the dance starting early. See playBoom().
//
// The background loop is a later addition and it gets the fuller kit, because a
// groove needs a drummer.

import { state } from './state.js';

const SEMITONES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/**
 * Frequency of a note name such as "C4", "Eb3" or "F#2". `semitones` transposes
 * it, which is how the pad reaches for the octave above a bass note.
 */
export function freq(name, semitones = 0) {
  const letter = name[0].toUpperCase();
  let rest = name.slice(1);
  let accidental = 0;

  if (rest.startsWith('b')) {
    accidental = -1;
    rest = rest.slice(1);
  } else if (rest.startsWith('#')) {
    accidental = 1;
    rest = rest.slice(1);
  }

  const midi = (Number(rest) + 1) * 12 + SEMITONES[letter] + accidental + semitones;
  return 440 * 2 ** ((midi - 69) / 12);
}

/** Tempo of the original tunes, and the grid every line below is written on. */
const TEMPO = 160;
const SIXTEENTH = 60 / TEMPO / 4;

/** Fade applied whenever a bus is cut short, so the cut does not click. */
const CUT_MS = 40;
/** How long a level change takes to arrive. Short on purpose: it is a slider, and the ear is
 *  dragging it. */
const VOLUME_MS = 60;

/**
 * How long the very first bus of a session takes to come up.
 *
 * Only the first one. Every other bus opens inside a piece that is already playing — a
 * loop, or the same song asked for a different way — and a piece that swelled every time
 * round would be a pump. This one is the only one that is arriving out of silence, and
 * arriving is a thing music does over a bar, not on a frame boundary.
 */
const FADE_IN_MS = 1200;

/**
 * How much of the music is left while the match is paused, and how long it takes
 * to get there.
 *
 * Not a mute and not a stop: the board is still on screen and you are still
 * reading it, and the tune under a pause is the match's own. It steps back far
 * enough to be clearly behind whatever you are doing and no further, and it does
 * it on a fader rather than a cut, because a hand on the volume knob is not a
 * change of scene. Two hundred milliseconds either way is a step, not a slide.
 *
 * A sixth of the way down in dB terms, near enough: about -4.4 dB, which is the
 * sort of move you notice behind a pause card without thinking the music broke.
 */
const DUCK_GAIN = 0.6;
const DUCK_MS = 200;

/** The winner's dance figure, written at the register the original used. */
const BURST = ['E3', 'F3', 'G3', 'E3', 'F3', 'D3', 'C3'];

/**
 * Makes the audio usable, and keeps it that way.
 *
 * Lazily creates the AudioContext, and then unlocks it. The old note on this
 * function was "must happen after a user gesture", and that is still true of the
 * first call — but the music is asked for at boot now, which means a context can
 * be created before anybody has touched the page. Such a context arrives SUSPENDED
 * and stays that way, and a suspended context is not merely silent: its clock is
 * not running, so the bars handed to it pile up against a standing clock and the
 * first thing anybody would hear is a heap of them all at once.
 *
 * So creating it and unlocking it are one job, and callers do not have to know
 * which side of a gesture they are on. The returned promise settles when the
 * browser is willing, which for an untouched page is when it is first touched.
 */
export function initAudio() {
  if (!state.audioCtx) {
    state.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }

  const ctx = state.audioCtx;
  // La salida se asegura aquí y no en cada voz: cualquier cosa que suene pasa por ella.
  masterNode(ctx);
  if (ctx.state === 'running') return Promise.resolve();

  return ctx.resume().catch(() => {});
}

/**
 * La salida del juego, en un solo sitio.
 *
 * Medido en el bus de la música con el volumen del jugador al 100 % (y el deslizador a 100): la
 * pieza sale a 0,138 de pico, diecisiete decibelios por debajo del techo. Una mezcla tan floja
 * obliga a poner el volumen del equipo a tope para oírla, y a tope cualquier altavoz distorsiona
 * en los golpes —que es el crujido, y sale igual en un móvil que en un PC porque lo que está a
 * tope son los dos—. Aquí se recupera ese margen de una vez para TODO (música y efectos juntos,
 * que es lo que mantiene la mezcla en su sitio) y sigue habiendo techo de sobra: la música pasa
 * antes por su compresor, que ve la señal sin subir.
 */
const MASTER_GAIN = 3;
function masterNode(audioCtx) {
  if (state.master && state.master.context === audioCtx) return state.master;

  const gain = audioCtx.createGain();
  gain.gain.value = MASTER_GAIN;
  gain.connect(audioCtx.destination);
  state.master = gain;
  return gain;
}

/** A dónde va cualquier voz que no diga otra cosa. */
function out() {
  if (state.master) return state.master;
  return state.audioCtx ? state.audioCtx.destination : null;
}

/**
 * Schedules a single enveloped oscillator note. `detune` is in cents: a second
 * copy of a line a few cents off is the cheapest way to make one voice sound like
 * several, and the quiet take's melody leans on it.
 */
export function note(freqHz, dur, when, type = 'square', vol = 0.032, dest = null, detune = 0) {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freqHz, when);
  osc.detune.value = detune;

  // The very short attack stops the square wave from clicking, and the steep
  // decay is what makes it read as a speaker beep instead of a held tone.
  const attack = Math.min(0.004, dur / 4);
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.linearRampToValueAtTime(vol, when + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + dur);

  osc.connect(gain).connect(dest || out());
  osc.start(when);
  osc.stop(when + dur + 0.02);
}

/**
 * Plays a monophonic line, one note at a time.
 * Each step is `[note name or null, length in units]`. Returns when it ends, so
 * callers can chain phrases and the music can measure its own loop.
 */
function line(steps, when, { vol = 0.032, dest = null, unit = SIXTEENTH, type = 'square' } = {}) {
  let t = when;
  for (const [name, length] of steps) {
    const dur = length * unit;
    // Notes are clipped slightly so consecutive ones articulate.
    if (name) note(freq(name), dur * 0.88, t, type, vol, dest);
    t += dur;
  }
  return t;
}

// --- one-shot bus ----------------------------------------------------------

/**
 * Un sub-bus filtrado: un biquad conectado a lo que le digan.
 *
 * Nació para la música —dos por compás, uno por sección— y a los efectos les vino bien tal cual:
 * `playBaa` le pasa la voz por un lowpass y `coinFilter` encadena dos. Vive aquí, con los otros
 * ayudantes de audio, porque es de los dos.
 */
function toneFilter(type, hz, dest) {
  const filter = state.audioCtx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = hz;
  filter.connect(dest);
  return filter;
}

/**
 * Every impact and jingle plays through a single gain node, so that a new level
 * can cut whatever is still ringing instead of letting it run on underneath the
 * fanfare. Same trick as the music bus: orphan the node and everything it has
 * queued goes silent at once.
 */
function oneShotBus() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return null;

  if (!state.shotBus) {
    state.shotBus = audioCtx.createGain();
    // Freshly built, so it arrives at the level the player has asked for rather than at 1.
    state.shotBus.gain.value = state.fxVolume;
    state.shotBus.connect(masterNode(audioCtx));
  }
  return state.shotBus;
}

/** Fades out and orphans the one-shot bus, silencing anything still playing. */
function cutOneShots() {
  const bus = state.shotBus;
  if (!bus) return;
  state.shotBus = null;

  const now = state.audioCtx.currentTime;
  bus.gain.setValueAtTime(bus.gain.value, now);
  bus.gain.linearRampToValueAtTime(0, now + CUT_MS / 1000);
  setTimeout(() => bus.disconnect(), CUT_MS + 60);
}

// --- sound effects ---------------------------------------------------------

/**
 * Banana leaves the fist: the four-note falling slide from the original.
 *
 * The most repeated sound in the game — it plays on every single throw — and measured, it
 * used to be the least subtle: four SQUARE voices at 0.025, as loud as the turn cue and
 * louder than the grenade's clink, in the 200-260Hz register where a square is a buzz rather
 * than a note, and running 830ms because each of the four notes was two sixteenths long. On
 * a sound that fires sixty times a match, all four of those add up to being told off.
 *
 * The four-note figure is the original's own gesture, so it stays. It is a triangle now — the
 * soft wave, the one the drone and the turn cue use — half as loud, and half as long.
 */
export function playThrow() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;
  line([['Ab3', 1], ['C4', 1], ['B3', 1], ['A#3', 1]], audioCtx.currentTime, { vol: 0.012, type: 'triangle', dest: oneShotBus() });
}

/**
 * A single low hit: a square that falls an octave in 60ms and then decays.
 *
 * The fall is the whole trick. A low note held flat reads as a note; the same
 * note sliding down reads as something hitting something. This is what makes an
 * impact sound like an impact without reaching for a noise source — the effects
 * in this file are square waves and nothing else, on purpose.
 */
function thud(when, { from, to, dur, vol, dest }) {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc.type = 'square';
  osc.frequency.setValueAtTime(from, when);
  osc.frequency.exponentialRampToValueAtTime(to, when + Math.min(0.06, dur / 3));

  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.linearRampToValueAtTime(vol, when + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + dur);

  osc.connect(gain).connect(dest);
  osc.start(when);
  osc.stop(when + dur + 0.02);
}

/**
 * Impact: one falling thump, with a crack on the front of it.
 *
 * This used to be the seven-note figure above, played as seven blips 47ms apart.
 * That is a machine-gun stutter, not a blast — and because it was the *same*
 * figure the victory dance plays, a direct hit sounded like the dance starting
 * early: twenty-one blips of the identical seven notes over 1.28 seconds, which
 * is what "prr prr prr" was. A blast is one event, so it is one event now.
 *
 * The descent is the original's own gesture — its impact figure ends falling
 * D3 to C3 — kept as a single sliding voice rather than seven separate ones.
 */
export function playBoom(big = false) {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const at = audioCtx.currentTime;
  const bus = oneShotBus();

  // The crack, then the body under it. Both start at the same instant, so the ear
  // hears one hit and not two.
  thud(at, { from: freq('G3'), to: freq('E3'), dur: 0.05, vol: big ? 0.045 : 0.03, dest: bus });
  thud(at, { from: freq('E3'), to: freq('C3'), dur: big ? 0.26 : 0.17, vol: big ? 0.055 : 0.036, dest: bus });
}

/**
 * A grenade leaving the fist: the pin, then a bugle call. There is no way to say
 * "fire in the hole" with an oscillator, so the bubble carries the words and the
 * bugle carries the delivery.
 */
export function playGrenade() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const bus = oneShotBus();
  const at = audioCtx.currentTime;

  // The pin coming out: a small bright click.
  note(freq('A6'), 0.03, at, 'square', 0.03, bus);

  // Then the call — up a fourth, down a fifth. That shape is every military
  // bugle figure there has ever been, which is why it reads as one.
  line([['C5', 1], ['F5', 2], ['C5', 1], ['G4', 3]], at + 0.07, { vol: 0.03, dest: bus });
}

/** A sheep, hopping. One bleat per hop, and the hop does the rest of the talking. */
export function playBaa() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const at = audioCtx.currentTime;
  const bus = oneShotBus();

  const osc = audioCtx.createOscillator();
  const wobble = audioCtx.createGain();
  const gain = audioCtx.createGain();

  // A saw a little above the speaking register, falling as a bleat falls.
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq('G3'), at);
  osc.frequency.linearRampToValueAtTime(freq('D3'), at + 0.16);

  // The flutter is the whole of the impression: a sheep is a tone with a fast tremolo on it,
  // about twenty of them a second. Two nodes, and it reads as an animal rather than a buzz.
  const lfo = audioCtx.createOscillator();
  const depth = audioCtx.createGain();
  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(19, at);
  depth.gain.value = 0.5;
  lfo.connect(depth).connect(wobble.gain);

  wobble.gain.value = 1;

  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.linearRampToValueAtTime(0.028, at + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.19);

  // Through a lowpass, like every other voice here that could otherwise be a beam: a saw at
  // 200 Hz would put its harmonics up where the reward sounds used to be.
  const soft = toneFilter('lowpass', 1200, bus);

  osc.connect(wobble).connect(gain).connect(soft);
  osc.start(at);
  osc.stop(at + 0.21);
  lfo.start(at);
  lfo.stop(at + 0.21);
}

/** A grenade off a wall: two clipped blips, high and close together. Metal, not music. */
export function playClink() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const at = audioCtx.currentTime;
  const bus = oneShotBus();

  note(freq('E5'), 0.045, at, 'square', 0.018, bus);
  note(freq('B5'), 0.03, at + 0.012, 'square', 0.011, bus);
}

/**
 * The reward sounds are squares, and a square at C7 carries its harmonics all the way up.
 *
 * Measured by firing the two of them on their own and holding the spectrum: the
 * achievement's third harmonic — 6279 Hz, and the loudest single thing above 5k in the whole
 * mix — stood 9 dB above the noise around it, with the fifth, seventh and ninth (10.5, 14.6
 * and 18.8 kHz) behind it, and the crate's own ladder starts at its fifth, 7840 Hz. That
 * ladder is what "something very high" sounds like when no noise source is involved, and it
 * is what these two sounds were.
 *
 * So the notes stay exactly where they are: the shape of a coin is two notes and the interval
 * between them, and that was never the problem. What goes is the ladder — two poles of
 * lowpass at 4.2 kHz, which leaves the fundamental untouched, keeps a little of the second
 * harmonic so the pair still reads as arcade rather than as a doorbell, and drops everything
 * above by enough that it is no longer the brightest thing the game says.
 */
const COIN_CUTOFF_HZ = 4200;

/** Two poles of lowpass for the reward sounds. Filter then filter, so the fall is 24 dB, not 12. */
function coinFilter(dest) {
  return toneFilter('lowpass', COIN_CUTOFF_HZ, toneFilter('lowpass', COIN_CUTOFF_HZ, dest));
}

/**
 * A reward: the one sound in the game allowed to be pleased with itself.
 *
 * Two notes, the second one higher and held — the shape every coin in every arcade has
 * ever had, which is why it needs no learning. Bright and square, and the loudest thing
 * in the mix, because it is reporting a present with a bow on it rather than a shot
 * landing or a turn changing hands.
 *
 * It is deliberately not the two notes of any particular coin: the shape is the public
 * part, and the notes here are a fifth in the key the piece is written in.
 */
export function playReward() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const bus = oneShotBus();
  const at = audioCtx.currentTime;
  const coin = coinFilter(bus);

  note(freq('C6'), 0.055, at, 'square', 0.045, coin);
  note(freq('G6'), 0.28, at + 0.05, 'square', 0.05, coin);
}

/**
 * An achievement unlocked: the reward's two notes and the octave above them.
 *
 * The same shape one step further up, because an achievement is the same news as a present
 * arriving and then some — something the player did that the game had been waiting for. The
 * two are meant to be read as a pair: the crate blips, the trophy blips higher and longer.
 */
export function playAward() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const bus = oneShotBus();
  const at = audioCtx.currentTime;
  const coin = coinFilter(bus);

  note(freq('C6'), 0.055, at, 'square', 0.045, coin);
  note(freq('G6'), 0.09, at + 0.05, 'square', 0.05, coin);
  note(freq('C7'), 0.34, at + 0.13, 'square', 0.05, coin);
}

/**
 * The drone arriving: a radar sweep, then the note it holds while it is up. Two
 * falling notes is what a UAV callout has sounded like for twenty years.
 */
export function playDrone() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  const bus = oneShotBus();
  const at = audioCtx.currentTime;

  note(freq('A4'), 0.11, at, 'triangle', 0.03, bus);
  note(freq('E4'), 0.17, at + 0.13, 'triangle', 0.03, bus);
  // And the rotor, low and square, underneath it.
  note(freq('D2'), 0.55, at + 0.3, 'sawtooth', 0.02, bus);
}

/**
 * The turn changing hands: two notes, the same two both ways round.
 *
 * Up when the turn has come to a person, down when it has gone to the machine. One
 * sound for both would have to be learned; the same pair backwards is understood the
 * first time, which is what a cue meant to be heard rather than listened to needs.
 *
 * A fifth, in the key the piece is written in, on a triangle, and the quietest thing
 * in the mix: this is said once a turn, underneath a match. It is not an event, it is
 * the board being handed over.
 */
export function playTurnGained() {
  turnCue(['C5', 'G5']);
}

export function playTurnLost() {
  turnCue(['G5', 'C5']);
}

/**
 * El tiempo del turno se ha agotado.
 *
 * El aviso de cambio de turno sonaba igual para todo —dos notas, hacia arriba o hacia abajo—,
 * así que quedarse sin tiempo se oía como si el turno se hubiera entregado a mano: nada decía
 * que se había acabado. Esto es un bip doble y repetido, en el mismo registro, que solo suena
 * cuando el reloj llega a cero. `oneShotBus` lo enruta con los demás avisos, así que un jingle
 * o un impacto lo cortan sin dejar nada sonando por debajo.
 */
export function playTimeUp() {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  line([['A5', 1], ['E5', 1], ['A5', 1], ['E5', 1]], audioCtx.currentTime, {
    vol: 0.042, dest: oneShotBus(), type: 'square',
  });
}

/**
 * Un tic del reloj: los últimos segundos del turno, uno por segundo.
 *
 * `playTimeUp` dice que ya se ha acabado, y hasta ahora no había nada que dijera que está a punto.
 * El reloj se pone en rojo (ver `TURN_RUSH_SECONDS`), que es un aviso mudo para quien está mirando
 * el campo y no la esquina; esto es el mismo aviso por el oído. Sube de tono en el último segundo
 * para que se distinga sin contarlos, que es lo que hace un reloj de verdad.
 *
 * No es una alarma: es un tic corto, y vive en el bus de un solo uso como todo lo demás, así que un
 * impacto o un cartel lo cortan sin dejar nada sonando por debajo.
 */
export function playTurnTick(urgent = false) {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  note(freq(urgent ? 'E6' : 'A5'), urgent ? 0.08 : 0.045, audioCtx.currentTime, 'square',
    urgent ? 0.036 : 0.026, oneShotBus());
}

/** Up or down, in the same two notes and the same two beats. */
function turnCue([first, second]) {
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  line([[first, 1], [second, 1]], audioCtx.currentTime, {
    vol: 0.026, dest: oneShotBus(), type: 'triangle',
  });
}

/** Title-screen fanfare: six eighth notes climbing, then a held cadence. */
export function playIntro() {
  const steps = ['C4', 'D4', 'E4', 'D4', 'C4', 'D4'].map(name => [name, 2]);
  steps.push(['E4', 4], ['C4', 4], ['C4', 4]);

  // A new level interrupts whatever is still ringing — usually the blast that
  // ended the last one — rather than talking over it.
  cutOneShots();

  playJingle(steps, 0.032);
}

/** Winner's dance: the impact figure thrown over and over, arms alternating. */
export function playVictory(swings = 8) {
  const steps = [];
  for (let i = 0; i < swings; i++) {
    BURST.forEach(name => steps.push([name, 0.5]));
    steps.push([null, 2]); // the beat between arm swings
  }

  // Waits for the impact to finish before starting. Both are built from the same
  // seven notes, so playing them together is just that figure twice at two
  // tempos — which is exactly what made the explosion sound like it dragged.
  playJingle(steps, 0.032, { delayMs: VICTORY_DELAY_MS });
}

// --- featured jingles ------------------------------------------------------

/** Delay before the victory dance, so the impact reads on its own first. */
const VICTORY_DELAY_MS = 420;

/**
 * Plays a featured line over the top of whatever is playing.
 *
 * Used to take the music out of the way and bring it back — it called `stopGroove` and timed the
 * return to the end of the line — which was something it could do while the music was a score this
 * file scheduled. The music is two files now, and an `<audio>` is not something this function can
 * stand aside for without stopping it, so the fanfare plays over the top: which is what a fanfare
 * should have been doing from the start.
 *
 * `delayMs` is in milliseconds — WebAudio's clock runs in seconds, and handing
 * it "380" instead of "0.38" schedules the line six minutes into the future.
 */
function playJingle(steps, vol, { delayMs = 0 } = {}) {
  initAudio();
  const audioCtx = state.audioCtx;
  if (!audioCtx) return;

  line(steps, audioCtx.currentTime + delayMs / 1000, { vol, dest: oneShotBus() });
}

const MUSIC_KEY = 'qbasicgorillas/music/v1';

/** The saved preference. Never throws: a refused read just means music. */
export function savedMusic() {
  try {
    return localStorage.getItem(MUSIC_KEY) !== 'off';
  } catch {
    return true;
  }
}

/**
 * The two levels, as the player last left them: music and effects, each from 0 to 1.
 *
 * One key beside the music switch, and kept out of the progress blob for the same reason it is:
 * a setting, not progress. Both default to 1, which is exactly the mix the game had before there
 * was anywhere to change it — so these can only ever take away, and a save from before they
 * existed sounds the way it always did.
 */
const VOLUME_KEY = 'qbasicgorillas/volume/v1';

const LEVELS = ['music', 'fx'];

/** 0 to 1, and anything that is not a number is the default rather than a silent game. */
function asLevel(v) {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
}

export function savedVolumes() {
  const out = { music: 1, fx: 1 };

  try {
    const raw = JSON.parse(localStorage.getItem(VOLUME_KEY) || '{}');
    LEVELS.forEach(key => { out[key] = asLevel(raw[key]); });
  } catch {
    // A refused read is the default mix, which is the one it shipped with.
  }

  return out;
}

/** Both are written together: they are two halves of one setting. */
function rememberVolumes() {
  try {
    localStorage.setItem(VOLUME_KEY, JSON.stringify({ music: state.musicVolume, fx: state.fxVolume }));
  } catch {
    // As with the music switch: a browser refusing to store a preference is not a reason to
    // stop the level applying for this session.
  }
}

/** The music level, taking effect on whatever is playing right now. */
export function setMusicVolume(v) {
  state.musicVolume = asLevel(v);
  rememberVolumes();

  // Las pistas, si son ellas las que están sonando: rampa corta para que arrastrar el deslizador
  // no dé un escalón. Es la única vía que queda, porque la música es sólo esto.
  TRACK_MOODS.forEach(mood => {
    if (trackEls[mood] && !trackEls[mood].paused) fadeTrack(mood, trackLevel(mood), VOLUME_MS);
  });
}

/**
 * The effects level. Every impact, clink and jingle in the game runs through one gain node, so
 * this is the whole of the effects mix.
 */
export function setFxVolume(v) {
  state.fxVolume = asLevel(v);
  rememberVolumes();

  const bus = state.shotBus;
  const audioCtx = state.audioCtx;
  if (!bus || !audioCtx) return;

  const now = audioCtx.currentTime;
  bus.gain.cancelScheduledValues(now);
  bus.gain.setValueAtTime(bus.gain.value, now);
  bus.gain.linearRampToValueAtTime(state.fxVolume, now + VOLUME_MS / 1000);
}

/**
 * True while the browser has not let this page make a sound yet.
 *
 * Nothing in the game can change the answer; what it can do is say so, because a
 * player waiting for music that is never going to arrive on its own has no way of
 * knowing that one touch would have done it.
 */
export function audioLocked() {
  return !state.audioCtx || state.audioCtx.state !== 'running';
}

/**
 * Starts the music at the first gesture, for a browser that would not let it start
 * on its own.
 *
 * Dropping the needle before anybody has touched anything is the browser's call and
 * not ours: a page that has not been interacted with gets a suspended AudioContext,
 * and no amount of code will make it sing. What can be done is to be ready — so the
 * music is one touch away rather than one menu away, and the touch that skips the
 * splash is also the one that starts the song.
 */
export function unlockMusicOnFirstGesture() {
  const go = () => {
    window.removeEventListener('pointerdown', go);
    window.removeEventListener('keydown', go);
    // Not setMusic alone: on a page the browser has refused to let make noise, the
    // context is still suspended at this moment, and the music has to wait for it.
    initAudio().then(() => setMusic(state.musicOn));
  };

  window.addEventListener('pointerdown', go, { once: true });
  window.addEventListener('keydown', go, { once: true });
}

/**
 * La música: dos ficheros, y nada más.
 *
 * `loud` es la portada y los menús, `ambient` la partida. Cada uno suena por su propio `<audio>`, y
 * eso trae una consecuencia que hay que respetar: **no pasan por `masterNode()`**, porque vienen
 * masterizados al techo (medido con `volumedetect`: pico 0,0 dBFS y media -17 dB en los dos) y el ×3
 * que necesitaba la pieza que este fichero sintetizaba los dejaría clipeando. Su nivel lo lleva
 * `el.volume`, y `TRACK_TRIM` es el techo que se les da.
 *
 * Aquí vivía un sintetizador de respaldo —una pieza entera, con su partitura— que entraba si el
 * fichero no estaba. Se quitó entero a petición: sus dos caminos de entrada (`startTrack`
 * devolviendo `false` y su `catch`) acababan sonando **encima** de los ficheros, que es justo lo que
 * se oía cuando alguien decía que «vuelve la música antigua». La música es esta y no hay otra: si un
 * fichero no está, ese momento se queda en silencio.
 */

/**
 * El techo de las pistas. Ver arriba: el ×3 del master no las toca.
 *
 * Bajado una vez más a petición: las dos suenan igual de bajas de lo que sonaban los MP3, y esta es
 * la palanca que las baja a la vez y en la misma proporción (0,4 sobre 0,5 son 2 dB menos, con el
 * reparto entre portada y partida donde estaba).
 */
const TRACK_TRIM = 0.4;

/**
 * Y del techo para abajo, cuánto suena cada momento. Las dos pistas son canciones ya mezcladas, así
 * que esta no es una decisión de mezcla de la música: es cuánto quiere el juego que suene en cada
 * sitio. La del menú va entera (0,7 sobre el techo, o sea 3 dB menos de lo que sonaba); la de la
 * partida se queda la mitad que esa, porque ahí la música comparte el oído con el reloj del turno,
 * el viento, el golpe del plátano y el cartel de la ronda: 0,35 contra 0,7, que son 6 dB de
diferencia. Y no hay más número que estos dos en todo el camino de la música de fondo.
 */
const TRACK_LEVELS = { loud: 0.7, ambient: 0.35 };

/** Lo que tarda el relevo de una pista a la otra. Ver `startTrack`. */
const TRACK_FADE_MS = 500;

const TRACK_MOODS = ['loud', 'ambient'];

/**
 * Y de dónde se piden.
 *
 * Los nombres dicen lo que son y no dónde se oyen, y eso es a propósito: el 30 de septiembre de 2026
 * hubo que cambiar los dos nombres sólo para mover la URL. El fichero que se sirve lleva el nombre
 * del fuente MÁS el hash de su contenido, así que renombrar el fuente cambia la URL y no el audio, y
 * eso fue lo que rescató a estos dos de la caché de Cloudflare: se habían servido con
 * `application/octet-stream` y sin `Accept-Ranges`, se quedaron cacheados con `max-age` de un año, y
 * el origen —ya arreglado, con `audio/mpeg` y 206 por trozos— no llegaba a oírse porque la CDN
 * contestaba ella antes. Con la URL nueva, la caché vieja no aplica y el arreglo se ve.
 */
const TRACK_SRC = {
  loud: new URL('./assets/tema-entrada.mp3', import.meta.url).href,
  ambient: new URL('./assets/tema-partida.mp3', import.meta.url).href,
};

const trackEls = { loud: null, ambient: null };
const trackTimers = { loud: null, ambient: null };
/** Una pista que no se ha podido reproducir. Se decide una vez y no se vuelve a intentar. */
const trackDead = { loud: false, ambient: false };

const clampLevel = v => Math.min(1, Math.max(0, v));

/** El elemento, creado a la primera. `preload` en `none`: no se pide ni un byte hasta que suena. */
function trackElement(mood) {
  if (trackEls[mood]) return trackEls[mood];

  const el = new Audio(TRACK_SRC[mood]);
  // `auto` y no `none`, y la diferencia se mide en segundos.
  //
  // Con `none` el navegador no pide ni un byte hasta que alguien llama a `play()` — y ese `play()`
  // necesita un gesto del jugador, así que en una pestaña recién abierta la descarga no empezaba
  // hasta el primer toque, con los 3,65 MB de la portada enteros por delante: de ahí que la música
  // tardara "como cinco segundos" en salir, con el servidor ya sirviendo por trozos. Con `auto` la
  // descarga arranca al cargar la página, en paralelo y sin bloquear nada, y para cuando hay gesto
  // ya hay audio que sonar: suena al toque.
  //
  // El coste es real y está elegido a sabiendas: quien abra la web se baja el tema que va a sonar.
  // Sólo uno, porque el elemento se crea la primera vez que su pista va a sonar — la portada precarga
  // la suya, y la de la partida empieza a bajar al entrar en una partida, no antes.
  el.preload = 'auto';
  el.loop = true;
  el.volume = 0;
  // Sólo "este fichero no vale" mata la pista, y es el código 4.
  //
  // Un `error` en un `<audio>` no significa "el fichero está roto": los códigos 1 a 3 son
  // abortado, red y decodificación —una recarga que corta la descarga, la red que se va, un
  // `<audio>` que pierde el foco— y son recuperables. Con `trackDead` puesto al PRIMER error, una
  // de esas dejaba la pista muerta para el resto de la sesión y el respaldo sintetizado entraba en
  // su lugar; eso es "vuelve la música antigua", y no se iba hasta recargar. Un 4, en cambio, es
  // el navegador diciendo que ese `src` no lo puede reproducir: eso sí es para siempre.
  el.addEventListener('error', () => {
    if (el.error && el.error.code === 4 /* MEDIA_ERR_SRC_NOT_SUPPORTED */) trackDead[mood] = true;
  });
  trackEls[mood] = el;
  return el;
}

/**
 * Donde tiene que estar el volumen de una pista: el jugador manda, la pausa manda, y del techo para
 * abajo lo reparten `TRACK_TRIM` y el nivel del momento.
 *
 * El nivel por momento vive en su propia tabla y no es el de ningún otro volumen del juego: las dos
 * son canciones ya mezcladas, así que esto no equilibra instrumentos, decide cuánto quiere el juego
 * que suene en cada momento.
 */
function trackLevel(mood) {
  return clampLevel(TRACK_TRIM * (TRACK_LEVELS[mood] ?? 1) * state.musicVolume * state.musicDuck);
}

/**
 * Sube o baja el volumen de una pista poco a poco.
 *
 * Con `pause` se para al llegar abajo, que es lo que hace el relevo: la que sale no se queda
 * sonando en silencio el resto de la partida. Son rampas sobre `volume` porque un elemento no
 * tiene ninguna: 25 ms entre pasos son veinte pasos en una rampa de medio segundo.
 */
function fadeTrack(mood, to, ms, { pause = false } = {}) {
  const el = trackEls[mood];
  if (!el) return;

  clearInterval(trackTimers[mood]);
  trackTimers[mood] = null;

  const from = el.volume;
  const target = clampLevel(to);

  if (ms <= 0 || Math.abs(target - from) < 0.001) {
    el.volume = target;
    // `!el.paused` porque este camino lo toman también los relevos de una pista que ya estaba
    // parada y a cero: parar lo que está parado no hace nada, pero ensucia el rastro de quien lo mire.
    if (pause && target === 0 && !el.paused) el.pause();
    return;
  }

  const t0 = performance.now();
  trackTimers[mood] = setInterval(() => {
    const k = Math.min(1, (performance.now() - t0) / ms);
    el.volume = clampLevel(from + (target - from) * k);
    if (k >= 1) {
      clearInterval(trackTimers[mood]);
      trackTimers[mood] = null;
      if (pause && target === 0) el.pause();
    }
  }, 25);
}

/**
 * La pista del momento entra y la otra sale, cruzadas.
 *
 * Este es el cambio que el sintetizador resolvía en el mismo compás y que dos ficheros no pueden:
 * son dos canciones distintas, así que el relevo es un cruce y no un cambio de instrumentación. La
 * que entra sigue por donde la dejó la última vez que sonó, que es lo que hace un reproductor.
 *
 * El cruce es lineal, y por eso las dos se reparten el techo en vez de sumarse: en el medio están a
 * la mitad cada una, así que dos ficheros a 0 dBFS no llegan a clipear en el relevo.
 *
 * Devuelve `false` cuando la pista está marcada como muerta —un fichero que el navegador ha dicho
 * que no puede reproducir— y `true` en cualquier otro caso.
 */
function startTrack(mood, { fade = TRACK_FADE_MS } = {}) {
  if (trackDead[mood]) return false;

  const el = trackElement(mood);
  const other = mood === 'loud' ? 'ambient' : 'loud';
  if (trackEls[other]) fadeTrack(other, 0, fade, { pause: true });

  el.volume = 0;
  fadeTrack(mood, trackLevel(mood), fade);

  const playing = el.play();
  if (playing && playing.catch) {
    playing.catch(err => {
      // Que el navegador no deje sonar sin un toque no es un fichero roto: se reintenta en el primer
      // gesto, que es justo lo que ya hace `unlockMusicOnFirstGesture`.
      if (err && err.name === 'NotAllowedError') return;

      // Y que lo interrumpa el relevo, tampoco. Pasar de la portada a la partida para la pista que
      // salía, y si su `play()` aún no había resuelto el navegador lo rechaza con `AbortError`: un
      // "ahora no", no un "esto no vale". Medido con la red retrasada a propósito (que es lo que hay
      // fuera de casa) — y era el camino por el que entraba el respaldo sintetizado, que ya no existe:
      // de ahí que este comentario hable de una música que ya no está.
      if (err && err.name === 'AbortError') return;

      // La última red, por si algún navegador lo cuenta de otra manera: si el elemento ya no es el
      // que suena, o está en pausa, lo que ha fallado no es el fichero.
      if (el.paused || trackEls[mood] !== el) return;

      // Y si el fichero no vale, esta pista se queda sin música. No hay respaldo que entrar: el
      // respaldo era el sintetizador y se quitó entero, a petición.
      trackDead[mood] = true;
    });
  }

  return true;
}

/** Apaga las dos pistas: la que suena y la que se quedó a medias. */
function stopTracks(ms = CUT_MS) {
  TRACK_MOODS.forEach(mood => fadeTrack(mood, 0, ms, { pause: true }));
}

/**
 * Turns the music on or off, and remembers it. Every path that changes the music
 * comes through here, so the preference cannot be updated in one place and not the
 * other.
 */
export function setMusic(on) {
  const playing = TRACK_MOODS.some(mood => trackEls[mood] && !trackEls[mood].paused);

  // Preguntar si ya está como tiene que estar no es un lujo: el primer gesto de una página nueva
  // dispara tres arranques (el del pie de página, el del desbloqueo por gesto y el del botón de
  // empezar) y sin esta guarda los tres llamaban a `play()`. Con un elemento por pista eso ya no
  // duplica la música —el mismo `<audio>` no suena dos veces por muchos `play()` que reciba— pero
  // sí reiniciaría la rampa de volumen tres veces en el mismo fotograma.
  //
  // La bandera `starting` que había aquí se fue con el sintetizador: existía porque arrancar la
  // música pasaba por `initAudio().then(...)` y el bus seguía nulo mientras el contexto se abría,
  // así que dos llamadas seguidas construían dos buses y la pieza salía duplicada. Un elemento de
  // audio no tiene ese problema.
  const alreadyRight = on === state.musicOn && (on ? playing : !playing);

  state.musicOn = on;

  try {
    localStorage.setItem(MUSIC_KEY, on ? 'on' : 'off');
  } catch {
    // A browser refusing to store a preference is not a reason to stop playing.
  }

  if (alreadyRight) return;

  // Los ficheros son la música, y son los únicos: no hay respaldo que arrancar detrás.
  if (on) startTrack(state.musicMood);
  else stopTracks();
}

/**
 * Switches between the front end's take and the match's.
 *
 * Two files, one moment each: `loud` is the front end and the menus, `ambient` the match — the same
 * two moments the score used to distinguish. `startTrack` crosses them, so the one that was playing
 * fades out and the other carries on from wherever it was left, which is what a player expects and
 * the one thing the synthetic version could not do without starting the piece over.
 */
export function setMusicMood(mood) {
  if (!TRACK_MOODS.includes(mood) || state.musicMood === mood) return;
  state.musicMood = mood;

  // La pista del momento, si es ella la que está sonando.
  if (state.musicOn) startTrack(mood);
}

/**
 * Steps the music back, or puts it back up, without touching the take.
 *
 * The pause screen is what this exists for. It is deliberately not a stop: the board is still on
 * screen and you are still reading it. It is one ramp on the file's own volume, by way of the same
 * `trackLevel` every other volume change goes through — which is what makes it land on the file
 * that is actually playing, whichever of the two that is.
 *
 * Nadie lo llama desde que la pausa dejó de bajar la música. Se queda por si algún día vuelve a
 * quererse: es la única palanca que baja la música sin pararla.
 *
 * Callers say whether the music should step back, not by how much: the amount is a
 * property of the music, and it lives with the rest of the mix at the top of this
 * file.
 */
export function setMusicDuck(on) {
  const level = on ? DUCK_GAIN : 1;
  if (state.musicDuck === level) return;
  state.musicDuck = level;

  // La pista que esté sonando, con su rampa corta.
  TRACK_MOODS.forEach(mood => {
    if (trackEls[mood] && !trackEls[mood].paused) fadeTrack(mood, trackLevel(mood), DUCK_MS);
  });
}
